import { useState } from 'react';
import { Save, DollarSign, Activity } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { useToast } from '../../../hooks/use-toast';
import { TooltipProvider } from '../../ui/tooltip';
import { InfoTip } from './InfoTip';
import cptConfigService, {
    CPTConfigValue,
    UpdateCPTConfigInput,
} from '../../../services/api/CPTAnalytics/CPTConfigService';

interface CPTConfigTabProps {
    config: CPTConfigValue | null;
    onConfigSaved: (config: CPTConfigValue) => void;
}


const parseNumber = (value: string): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

export default function CPTConfigTab({
    config,
    onConfigSaved,
}: CPTConfigTabProps) {
    const { toast } = useToast();
    const [saving, setSaving] = useState(false);

    const [rvuTarget, setRvuTarget] = useState(
        config?.monthlyRvuTarget ? String(config.monthlyRvuTarget) : ''
    );
    const [revenueTarget, setRevenueTarget] = useState(
        config?.monthlyRevenueTarget ? String(config.monthlyRevenueTarget) : ''
    );
    const [codeConfigs, setCodeConfigs] = useState(
        (config?.cptCodeConfigs ?? []).map((c) => ({
            cptCode: c.cptCode,
            rvuPerUnit: c.rvuPerUnit ? String(c.rvuPerUnit) : '',
            reimbursementPerUnit: c.reimbursementPerUnit
                ? String(c.reimbursementPerUnit)
                : '',
        }))
    );

    const updateCodeConfig = (
        index: number,
        field: 'rvuPerUnit' | 'reimbursementPerUnit',
        value: string
    ) => {
        setCodeConfigs((prev) =>
            prev.map((entry, i) =>
                i === index ? { ...entry, [field]: value } : entry
            )
        );
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload: UpdateCPTConfigInput = {
                monthlyRvuTarget: parseNumber(rvuTarget),
                monthlyRevenueTarget: parseNumber(revenueTarget),
                cptCodeConfigs: codeConfigs.map((entry) => ({
                    cptCode: entry.cptCode,
                    rvuPerUnit: parseNumber(entry.rvuPerUnit),
                    reimbursementPerUnit: parseNumber(
                        entry.reimbursementPerUnit
                    ),
                })),
            };

            const saved = await cptConfigService.updateCPTConfig(payload);
            onConfigSaved(saved);

            toast({
                title: 'Config saved',
                description: 'CPT analytics configuration has been updated.',
            });
        } catch {
            toast({
                variant: 'destructive',
                title: 'Save failed',
                description: 'Failed to save CPT analytics configuration.',
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <TooltipProvider delayDuration={300}>
            <div className="mt-6 space-y-10">
                {/* Monthly Targets */}
                <section>
                    <div className="mb-4">
                        <h2 className="text-xl font-semibold">
                            Monthly Targets
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            Set your department's monthly goals. These are used
                            to track progress on the analytics tab.
                        </p>
                    </div>

                    <Card className="border-subtle">
                        <CardContent className="grid grid-cols-1 gap-6 p-5 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label
                                    htmlFor="rvu-target"
                                    className="flex items-center gap-1.5 text-sm text-foreground"
                                >
                                    <Activity className="h-3.5 w-3.5" />
                                    RVU Estimate / Month (Optional)
                                    <InfoTip id="config-rvu-target" text="Your department's monthly estimates for total Relative Value Units. Used to track progress on the analytics tab." />
                                </Label>
                                <Input
                                    id="rvu-target"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="e.g. 50"
                                    value={rvuTarget}
                                    onChange={(e) =>
                                        setRvuTarget(e.target.value)
                                    }
                                />
                            </div>
                            <div className="space-y-2">
                                <Label
                                    htmlFor="revenue-target"
                                    className="flex items-center gap-1.5 text-sm text-foreground"
                                >
                                    <DollarSign className="h-3.5 w-3.5" />
                                    Revenue Estimate / Month (Optional)
                                    <InfoTip id="config-revenue-target" text="Your department's monthly estimates for revenue. Compared against actual revenue from completed cases." />
                                </Label>
                                <Input
                                    id="revenue-target"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="e.g. 700"
                                    value={revenueTarget}
                                    onChange={(e) =>
                                        setRevenueTarget(e.target.value)
                                    }
                                />
                            </div>
                        </CardContent>
                    </Card>
                </section>

                {/* CPT Code Rates */}
                <section>
                    <div className="mb-4">
                        <h2 className="text-xl font-semibold">
                            CPT Code Rates
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            Enter the RVU weight and expected reimbursement for
                            each CPT code. These values are used to calculate
                            estimated revenue for your reports.
                        </p>
                    </div>

                    <Card className="border-subtle">
                        <CardContent className="p-0">
                            <div className="grid grid-cols-[80px_1fr_1fr] items-center gap-x-4 border-b border-subtle px-5 py-3 text-xs uppercase tracking-wide text-subtle-foreground font-medium">
                                <span>Code</span>
                                <span className="flex items-center gap-1.5">
                                    RVU / Unit
                                    <InfoTip id="config-rvu-per-unit" text="The RVU value assigned to one unit of this CPT code." />
                                </span>
                                <span className="flex items-center gap-1.5">
                                    Reimbursement / Unit ($)
                                    <InfoTip id="config-reimbursement-per-unit" text="The expected reimbursement in USD for one unit of this CPT code." />
                                </span>
                            </div>
                            {codeConfigs.map((entry, index) => (
                                <div
                                    key={entry.cptCode}
                                    className="grid grid-cols-[80px_1fr_1fr] items-center gap-x-4 border-b border-subtle px-5 py-3 last:border-b-0"
                                >
                                    <span className="w-fit text-sm font-semibold bg-zinc-700 px-2 py-1 rounded-md">
                                        {entry.cptCode}
                                    </span>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="e.g. 0.33"
                                        value={entry.rvuPerUnit}
                                        onChange={(e) =>
                                            updateCodeConfig(
                                                index,
                                                'rvuPerUnit',
                                                e.target.value
                                            )
                                        }
                                    />
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="e.g. 11.00"
                                        value={entry.reimbursementPerUnit}
                                        onChange={(e) =>
                                            updateCodeConfig(
                                                index,
                                                'reimbursementPerUnit',
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>
                            ))}
                            {codeConfigs.length === 0 && (
                                <div className="px-5 py-8 text-center text-sm text-foreground">
                                    No CPT codes configured.
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </section>

                <div className="flex justify-end">
                    <Button onClick={handleSave} disabled={saving}>
                        <Save className="mr-1.5 h-4 w-4" />
                        {saving ? 'Saving...' : 'Save Config'}
                    </Button>
                </div>
            </div>
        </TooltipProvider>
    );
}
